/**
 * BIATCH® Lounge — rail pagination + featured swap + share / print.
 *
 * Every recipe is pre-rendered twice (full featured card + small rail
 * card). Both copies carry the same `data-recipe-id`. Exactly one
 * featured card has `.is-active` (visible), and the matching rail
 * card has `.is-active` (highlighted — to show it's the featured one).
 *
 * Interactions:
 *   - Click `>` / `<`  → scroll the rail viewport by one page (2 cards),
 *                        wrapping at both ends ("rotate in a circle").
 *   - Click a rail card → that recipe is promoted into the featured slot.
 *       All rail cards stay in place (no swap); only `.is-active` moves,
 *       which highlights the new card and updates the featured card above.
 *
 * Share: navigator.share() when available, otherwise clipboard with
 * a transient "COPIED" state.
 */
(function () {
  if (typeof document === 'undefined') return;

  var initRecipes = function (section) {
    if (section.dataset.cocktailRecipesBound === '1') return;
    section.dataset.cocktailRecipesBound = '1';

    var cards = section.querySelectorAll('.lounge__card[data-recipe-id]');
    var railCards = section.querySelectorAll('.lounge__rail-card[data-recipe-id]');
    var viewport = section.querySelector('[data-cocktail-rail-viewport]');
    var prevBtn = section.querySelector('[data-cocktail-rail-prev]');
    var nextBtn = section.querySelector('[data-cocktail-rail-next]');

    if (railCards.length === 0) return;

    // --- Featured select --------------------------------------------------
    // Clicking a rail card just promotes that recipe into the featured slot.
    // Every rail card stays in place (no swap); the active one is highlighted
    // via CSS so the user sees which recipe is currently featured.
    // Hidden cards ship their poster as `data-poster` so 14 unseen images
    // aren't fetched to display one. Swap it in before the card is shown.
    var showPoster = function (card) {
      if (!card) return;
      var video = card.querySelector('video[data-poster]');
      if (!video) return;
      video.poster = video.getAttribute('data-poster');
      video.removeAttribute('data-poster');
    };

    var cardById = function (id) {
      return section.querySelector('.lounge__card[data-recipe-id="' + id + '"]');
    };

    var activate = function (newRail) {
      if (newRail.classList.contains('is-active')) return;

      var newId = newRail.dataset.recipeId;
      showPoster(cardById(newId));
      cards.forEach(function (el) {
        el.classList.toggle('is-active', el.dataset.recipeId === newId);
      });
      railCards.forEach(function (el) {
        el.classList.toggle('is-active', el.dataset.recipeId === newId);
      });
    };

    var scrollToFeatured = function (id) {
      var card = section.querySelector('.lounge__card[data-recipe-id="' + id + '"]');
      if (!card) return;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      card.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    };

    railCards.forEach(function (card) {
      var btn = card.querySelector('[data-cocktail-rail-select]');
      if (!btn) return;
      // Start the poster fetch on intent rather than on click, so the swap
      // still looks instant despite the image no longer being preloaded.
      var warm = function () {
        showPoster(cardById(card.dataset.recipeId));
      };
      btn.addEventListener('pointerenter', warm, { once: true });
      btn.addEventListener('touchstart', warm, { once: true, passive: true });
      btn.addEventListener('focus', warm, { once: true });
      btn.addEventListener('click', function () {
        activate(card);
        scrollToFeatured(card.dataset.recipeId);
      });
    });

    // Deep link (?section=<section id>&recipe=<recipe id>) — select the
    // shared recipe in the matching section instance and scroll to it.
    var params = new URLSearchParams(window.location.search);
    var linkedRecipe = params.get('recipe');
    var linkedSection = params.get('section');
    if (linkedRecipe !== null && linkedSection === section.dataset.sectionId) {
      var linkedRail = null;
      railCards.forEach(function (card) {
        if (card.dataset.recipeId === linkedRecipe) linkedRail = card;
      });
      if (linkedRail) {
        activate(linkedRail);
        scrollToFeatured(linkedRecipe);
      }
    }

    // --- Rail pagination with continuous rotation ------------------------
    //
    // At a normal scroll position we just `scrollBy(±clientWidth)` and let
    // scroll-snap do its thing.
    //
    // At the edges we *recycle* the page-worth of visible cards across the
    // far side by mutating their CSS `order`, then reset `scrollLeft` so
    // the visible content is unchanged, then smooth-scroll one page in the
    // requested direction. The user sees one continuous forward motion
    // instead of a snap back to the start.
    //
    // `style.order` is already on every rail card (set in Liquid and
    // mutated by the click-swap), so the two interactions cooperate.
    var CARDS_PER_PAGE = 2; // matches `flex: 0 0 calc((100% - 24px) / 2)`

    var visibleRailsByOrder = function () {
      return Array.prototype.slice.call(railCards)
        .sort(function (a, b) {
          return parseInt(a.style.order, 10) - parseInt(b.style.order, 10);
        });
    };

    var page = function (direction) {
      if (!viewport) return;
      var max = viewport.scrollWidth - viewport.clientWidth;
      if (max <= 0) return;

      var atEnd = direction > 0 && viewport.scrollLeft >= max - 1;
      var atStart = direction < 0 && viewport.scrollLeft <= 1;

      if (!atEnd && !atStart) {
        viewport.scrollBy({ left: direction * viewport.clientWidth, behavior: 'smooth' });
        return;
      }

      var visible = visibleRailsByOrder();
      if (visible.length < 2) {
        viewport.scrollTo({ left: direction > 0 ? 0 : max, behavior: 'smooth' });
        return;
      }
      var n = Math.min(CARDS_PER_PAGE, visible.length);

      if (direction > 0) {
        // Recycle leading cards to the trailing end.
        var maxOrder = parseInt(visible[visible.length - 1].style.order, 10);
        for (var i = 0; i < n; i++) {
          visible[i].style.order = (maxOrder + i + 1).toString();
        }
        viewport.scrollLeft = 0;
      } else {
        // Recycle trailing cards to the leading end.
        var minOrder = parseInt(visible[0].style.order, 10);
        for (var j = 0; j < n; j++) {
          visible[visible.length - 1 - j].style.order = (minOrder - j - 1).toString();
        }
        viewport.scrollLeft = viewport.scrollWidth - viewport.clientWidth;
      }

      // RAF lets the style.order + scrollLeft writes paint together before
      // the smooth-scroll starts, so the user never sees the silent reset.
      requestAnimationFrame(function () {
        viewport.scrollBy({ left: direction * viewport.clientWidth, behavior: 'smooth' });
      });
    };

    if (prevBtn) prevBtn.addEventListener('click', function () { page(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { page(1); });
  };

  var initShare = function (btn) {
    if (btn.dataset.cocktailShareBound === '1') return;
    btn.dataset.cocktailShareBound = '1';

    btn.addEventListener('click', function () {
      // Share a deep link to this recipe, not the bare page URL.
      var url = window.location.origin + window.location.pathname;
      var card = btn.closest('[data-recipe-id]');
      var sec = btn.closest('[data-cocktail-recipes]');
      if (card && sec && sec.dataset.sectionId) {
        url +=
          '?section=' + encodeURIComponent(sec.dataset.sectionId) +
          '&recipe=' + encodeURIComponent(card.dataset.recipeId);
      }
      var title = btn.dataset.shareTitle || document.title;

      var showCopied = function () {
        btn.dataset.copied = '1';
        var label = btn.querySelector('.lounge__action-label');
        var original = label ? label.textContent : null;
        if (label) label.textContent = 'COPIED';
        setTimeout(function () {
          btn.removeAttribute('data-copied');
          if (label && original !== null) label.textContent = original;
        }, 1800);
      };

      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(function () { /* dismissed */ });
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(showCopied, function () { /* denied */ });
        return;
      }
      var temp = document.createElement('input');
      temp.value = url;
      document.body.appendChild(temp);
      temp.select();
      try { document.execCommand('copy'); showCopied(); } catch (e) { /* unsupported */ }
      document.body.removeChild(temp);
    });
  };

  var initPrint = function (btn) {
    if (btn.dataset.cocktailPrintBound === '1') return;
    btn.dataset.cocktailPrintBound = '1';
    btn.addEventListener('click', function () {
      var card = btn.closest('.lounge__card');
      if (!card) {
        window.print();
        return;
      }

      // Print only this recipe card: hide every sibling along the card's
      // ancestor chain (keeps layout collapsed — no blank pages), force
      // its accordions open, and restore everything after printing.
      var hidden = [];
      var opened = [];

      card.querySelectorAll('details:not([open])').forEach(function (d) {
        d.setAttribute('open', '');
        opened.push(d);
      });

      var node = card;
      while (node && node !== document.body && node.parentElement) {
        var parent = node.parentElement;
        Array.prototype.forEach.call(parent.children, function (sib) {
          if (sib !== node) {
            sib.classList.add('print-hidden');
            hidden.push(sib);
          }
        });
        node = parent;
      }

      document.body.classList.add('printing-recipe');

      var cleanup = function () {
        window.removeEventListener('afterprint', cleanup);
        document.body.classList.remove('printing-recipe');
        hidden.forEach(function (el) { el.classList.remove('print-hidden'); });
        opened.forEach(function (d) { d.removeAttribute('open'); });
      };
      window.addEventListener('afterprint', cleanup);

      window.print();
    });
  };

  var bind = function (root) {
    (root || document).querySelectorAll('[data-cocktail-recipes]').forEach(initRecipes);
    (root || document).querySelectorAll('[data-cocktail-share]').forEach(initShare);
    (root || document).querySelectorAll('[data-cocktail-print]').forEach(initPrint);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(); });
  } else {
    bind();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target;
    if (section && section.querySelector && section.querySelector('[data-cocktail-recipes]')) {
      bind(section);
    }
  });
})();
